import { cn } from '@/shared/lib/utils'
import { CONNECTIVITY_OPTIONS, type ConnectivityValue } from '../lib/account-connectivity'

interface WizardConnectivityRadioGroupProps {
  connectivity: ConnectivityValue
  isEditing: boolean
  // Edit mode only: whether this account may switch to another connection (UC-094).
  canChangeConnectivity: boolean
  onChange: (connectivity: ConnectivityValue) => void
}

/** Single-select connectivity radio group used by the configure step. While
 * editing, an option is locked (visually dimmed, non-interactive) unless the
 * account may change its connection, and Open Banking is never a target: it is
 * only reached by connecting the bank (UC-094). */
export function WizardConnectivityRadioGroup({
  connectivity, isEditing, canChangeConnectivity, onChange,
}: WizardConnectivityRadioGroupProps) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-2">Connection</p>
      <div role="radiogroup" aria-label="Connectivity type" className="flex flex-wrap gap-x-5 gap-y-2">
        {CONNECTIVITY_OPTIONS.map((option) => {
          const isSelected = option.value === connectivity
          const isLocked = isEditing && (!canChangeConnectivity || option.isOpenBanking)
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-disabled={isLocked}
              tabIndex={isLocked ? -1 : 0}
              onClick={isLocked ? undefined : () => onChange(option.value)}
              onKeyDown={isLocked ? undefined : (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onChange(option.value)
                }
              }}
              className={cn(
                'flex items-center gap-2 text-sm transition-colors',
                isLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
              )}
            >
              <span
                className={cn(
                  'flex h-4 w-4 items-center justify-center rounded-full border',
                  isSelected ? 'border-primary' : 'border-muted-foreground',
                )}
              >
                {isSelected && <span className="h-2 w-2 rounded-full bg-primary" />}
              </span>
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
